import PropTypes from 'prop-types';
import React, { Component } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { once } from 'lodash';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import { getFlag } from '../../configureFlags';

import Betslip from '../../containers/betslip/Betslip';
import Multiples from '../../containers/Multiples';
import Account from '../../containers/account/Account';
import Panel from './Panel';
import ReleaseNotes from './ReleaseNotes';
import Bar from '../../containers/RightSidebar/Bar';
import Settings from '../../containers/RightSidebar/Settings';
import Podcast from '../Podcast';
import AnimatedAudioIcon from '../Podcast/AnimatedAudioIcon';
import SVGIcon from '../widgets/SVGIcon';
import IconMultiples from '../../../images/icons/misc/icon-multiples.svg';
import IconPodcast from '../../../images/icons/misc/icon-podcast.svg';
import { track } from '@xanadu/ui-shared/interfaces/woopra';
import { RELEASE_DATE, PODCAST_USER_RETURN_THRESHOLD, MOBILE_BREAKPOINT } from '../../constants';

import styles from './RightSidebar.module.scss';
import panelStyles from '../account/Panel/Panel.module.scss';

const RELEASE_NOTES_DEADLINE = moment(RELEASE_DATE, 'DD-MM-YYYY').add(8, 'days');
const isBeforeDeadline = moment().isBefore(RELEASE_NOTES_DEADLINE);

let podcastMountedOnce = false;
const localStorageUiVersion = getFlag('mbui-version');

class RightSidebar extends Component {
  static get propTypes() {
    return {
      logout: PropTypes.func.isRequired,
      username: PropTypes.string.isRequired,
      lastLogin: PropTypes.string.isRequired,
      sidebarContent: PropTypes.string,
      topPosition: PropTypes.number,
      bannerMessageVisible: PropTypes.bool,
      updateRightSidebar: PropTypes.func.isRequired,
      sidebarVisible: PropTypes.bool.isRequired,
      isDesktop: PropTypes.bool.isRequired,
      cinemaMode: PropTypes.bool.isRequired,
      uiVersion: PropTypes.string,
      widthSize: PropTypes.number,
      children: PropTypes.node,
      displayModal: PropTypes.func.isRequired,
      location: PropTypes.shape({
        pathname: PropTypes.string.isRequired
      }),
    };
  }

  static get defaultProps() {
    return {
      location: {}
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      releaseBoxAnimation: false,
      showGiftBox: false,
      podcastIsPlaying: false,
      hidePodcastPanel: false
    };

    this.handleReleaseBoxAnimation = once(this.handleReleaseBoxAnimation);
    this.releaseBoxIcon = React.createRef();
    this.animateBoxTimeout = null;
    this.animateBoxTimeout2 = null;
    this.animateBoxInfiniteInterval = null;
    this.podcastUserReturnInterval = undefined;
    this.podcastUserReturnTimeThreshold = PODCAST_USER_RETURN_THRESHOLD;
  }

  componentDidMount() {
    if (isBeforeDeadline) {
      this.handleReleaseBoxAnimation();
    }
  }

  componentDidUpdate(prevProps) {
    const { podcastIsPlaying } = this.state;

    if (!podcastIsPlaying) {
      const { sidebarContent, sidebarVisible } = this.props;
      const {
        sidebarContent: prevSidebarContent,
        sidebarVisible: prevSidebarVisible
      } = prevProps;

      const prevPodcastTab = prevSidebarContent === 'podcast';
      const podcastTab = sidebarContent === 'podcast';

      const enterPodcastView = !prevPodcastTab && podcastTab;
      const closePodcastView = prevSidebarVisible && !sidebarVisible && podcastTab;
      const exitPodcastView = prevPodcastTab && !podcastTab;

      if (enterPodcastView || exitPodcastView || closePodcastView) {
        clearInterval(this.podcastUserReturnInterval);
        this.podcastUserReturnInterval = undefined;

        this.podcastReturnTimeThreshold = PODCAST_USER_RETURN_THRESHOLD;
      }

      if (exitPodcastView || closePodcastView) {
        this.podcastUserReturnInterval = setInterval(this.handlePodcastUserReturnTimer, 1000);
      }

      if (podcastTab && sidebarVisible) {
        track('podcast', 'Open Podcast panel');
      }

      if (exitPodcastView || (podcastTab && !sidebarVisible)) {
        track('podcast', 'Close Podcast panel');
      }
    }

    if (isBeforeDeadline) {
      this.handleReleaseBoxAnimation();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.animateBoxTimeout);
    clearTimeout(this.animateBoxTimeout2);
    clearTimeout(this.animateBoxInfiniteInterval);
    clearInterval(this.podcastUserReturnInterval);
    this.animateBoxTimeout = null;
    this.animateBoxTimeout2 = null;
    this.animateBoxInfiniteInterval = null;
    this.podcastUserReturnInterval = null;
  }

  getItems = () => {
    const {
      releaseBoxAnimation,
      showGiftBox,
      podcastIsPlaying,
    } = this.state;

    const {
      username,
      lastLogin,
      logout,
      location,
      isDesktop,
    } = this.props;

    const items = {
      betslip: {
        icon: (
          <SVGIcon
            name="betslip"
            className={styles.buttonIcon}
            onClick={e => e.preventDefault()}
            style={{ width: '16px' }}
          />
        ),
        component: (<Betslip />),
        name: 'My Bets',
        hook: 'rhsmybets'
      },
      multiples: {
        icon: (
          <IconMultiples
            className={styles.buttonIcon}
            onClick={e => e.preventDefault()}
          />
        ),
        component: (<Multiples />),
        name: 'Multiples',
        hook: 'rhsmultiples'
      },
      account: {
        icon: (
          <SVGIcon
            name="account"
            className={styles.buttonIcon}
            onClick={e => e.preventDefault()}
          />
        ),
        component: (
          <Account
            title={username}
            lastLogin={lastLogin}
            logout={logout}
            account
            previousURL={location.pathname}
          />
        ),
        name: 'Account',
        hook: 'rhsaccount'
      },
      settings: {
        icon: (
          <SVGIcon
            name="settings"
            className={styles.buttonIcon}
            onClick={e => e.preventDefault()}
          />
        ),
        component: (
          <Panel
            className={panelStyles['panel--main']}
            mainPanel
            title="Settings"
          >
            <Settings />
          </Panel>
        ),
        name: 'Settings',
        hook: 'rhs-settings'
      }
    };

    if (isDesktop) {
      items.podcast = {
        icon: podcastIsPlaying ? (
          <AnimatedAudioIcon />
        ) : (
          <IconPodcast
            className={styles.buttonIcon}
            onClick={e => e.preventDefault()}
          />
        ),
        component: null,
        name: 'Podcast',
        hook: 'rhs-podcast'
      };
    }

    const releaseIconClassNames = [styles.buttonIcon, `${styles['buttonIcon--release']}`];

    if (showGiftBox && releaseBoxAnimation) {
      releaseIconClassNames.push(`${styles['buttonIcon--animate']}`);
    }

    if (showGiftBox && isBeforeDeadline) {
      items.newrelease = {
        icon: (
          <SVGIcon
            name="icon-present"
            className={releaseIconClassNames.join(' ')}
            onClick={e => e.preventDefault()}
            ref={this.releaseBoxIcon}
          />
        ),
        component: (
          <Panel
            className={panelStyles['panel--main']}
            mainPanel
            title="New on Matchbook"
            subTitle={moment(RELEASE_DATE, 'DD-MM-YYYY').format('Do MMMM YYYY')}
          >
            <ReleaseNotes
              releaseDate={RELEASE_DATE}
              displayModal={this.props.displayModal}
            />
          </Panel>
        ),
        name: 'New!',
        hook: 'rhsnew'
      };
    }

    return items;
  };

  getComponent = content => (this.getItems()[content] ? this.getItems()[content].component : null);

  handlePodcastUserReturnTimer = () => {
    this.podcastReturnTimeThreshold = this.podcastReturnTimeThreshold - 1000;

    if (this.podcastReturnTimeThreshold === 0) {
      clearInterval(this.podcastUserReturnInterval);

      this.setState({ hidePodcastPanel: true });
    }
  };

  handleReleaseBoxAnimation = () => {
    const { showGiftBox } = this.state;
    const { uiVersion } = this.props;
    const isNewVersion = uiVersion !== localStorageUiVersion;

    try {
      const sameReleaseDate = localStorage.getItem('release-date') === RELEASE_DATE;

      if (!showGiftBox) {
        if (isNewVersion && !sameReleaseDate) {
          this.animateBoxTimeout = setTimeout(() => {
            this.animateReleaseBox();
          }, 4000);

          localStorage.setItem('release-info-acknowledge', 'false');
          localStorage.setItem('release-date', RELEASE_DATE);
        } else {
          this.animateReleaseBox();
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Could not access to localStorage', e);
    }
  };

  animateReleaseBox = () => {
    this.setState({
      showGiftBox: true,
    });

    try {
      const releaseInfoAcknowledged = localStorage.getItem('release-info-acknowledge') === 'true';
      const sameReleaseDate = localStorage.getItem('release-date') === RELEASE_DATE;

      this.animateBoxTimeout2 = setTimeout(() => {
        this.setState({
          releaseBoxAnimation: true,
        });

        if (!releaseInfoAcknowledged && sameReleaseDate) {
          this.animateBoxInfiniteInterval = setInterval(() => {
            if (this.releaseBoxIcon) {
              this.releaseBoxIcon.current.classList.add(styles['buttonIcon--animateInfinite']);
            }

            this.animateBoxTimeout2 = setTimeout(() => {
              if (this.releaseBoxIcon) {
                this.releaseBoxIcon.current.classList.remove(styles['buttonIcon--animateInfinite']);
              }
            }, 1000);
          }, 30000);
        }
      }, 50);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Could not access to localStorage', e);
    }
  };

  handleButtonClick = (panelOpened) => {
    const {
      sidebarContent: prevSidebarContent,
      sidebarVisible: prevSidebarVisible,
      updateRightSidebar,
    } = this.props;

    let visible;

    if (panelOpened === 'newrelease') {
      track('Right Sidebar', 'New Releases Click', {
        description: panelOpened
      });

      clearInterval(this.animateBoxInfiniteInterval);
      this.animateBoxInfiniteInterval = null;

      try {
        localStorage.setItem('release-info-acknowledge', 'true');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Could not access to localStorage', e);
      }
    }

    if (prevSidebarContent === panelOpened) {
      visible = !prevSidebarVisible;
    } else {
      visible = true;
    }

    if (panelOpened === 'podcast') {
      podcastMountedOnce = true;

      this.setState({
        hidePodcastPanel: false,
      });
    }

    updateRightSidebar({
      content: panelOpened,
      visible
    });
  };

  handlePodcastPlayerState = (podcastIsPlaying) => {
    this.setState({ podcastIsPlaying, hidePodcastPanel: false });
  };

  render() {
    const {
      cinemaMode,
      sidebarContent,
      sidebarVisible,
      topPosition,
      bannerMessageVisible,
      widthSize,
      isDesktop,
      children,
    } = this.props;

    const { podcastIsPlaying, hidePodcastPanel } = this.state;

    const items = this.getItems();
    const panelComponent = this.getComponent(sidebarContent);
    const panelClasses = [styles.panel];
    const extraOffset = widthSize > MOBILE_BREAKPOINT ? 24 : 48;

    const podcastClass = classNames(
      styles.containerPodcastPanel,
      { [styles['containerPodcastPanel--visible']]: sidebarVisible && sidebarContent === 'podcast' }
    );

    const podcastPanelClass = classNames(
      panelStyles['panel--main'],
      styles.podcastPanel
    );

    const mainClasses = classNames({
      [styles.main]: true,
      [styles['main--cinemaMode']]: cinemaMode,
    });

    let podcastPanel = isDesktop && podcastMountedOnce && (
      <div className={podcastClass}>
        <div className={styles.content}>
          <Panel
            className={podcastPanelClass}
            noHeader
            mainPanel
            title="Podcast"
          >
            <Podcast onPlayerStateChange={this.handlePodcastPlayerState} />
          </Panel>
        </div>
      </div>
    );

    const podcastTab = sidebarContent === 'podcast';
    let style = {};
    let panel = null;

    if (topPosition && bannerMessageVisible) {
      style = {
        top: widthSize < 481 ? topPosition : (topPosition - extraOffset)
      };
    }

    if (!podcastTab || (!sidebarVisible && podcastTab)) {
      if (!podcastIsPlaying && hidePodcastPanel) {
        podcastPanel = null;
      }
    }

    if (sidebarVisible && !podcastTab) {
      panel = (
        <CSSTransition
          key={sidebarContent}
          classNames={{
            enter: styles['panel--enter'],
            enterActive: styles['panel--enter-active'],
            enterDone: styles['panel--enter-done'],
            exit: styles['panel--exit'],
            exitActive: styles['panel--exit-active'],
            exitDone: styles['panel--exit-done'],
          }}
          // Note: styles.durationPanelAnimation MUST BE in `ms`
          timeout={parseInt(styles.durationPanelAnimation, 10)}
        >
          <div
            className={panelClasses.join(' ')}
          >
            <div
              className={styles.content}
            >
              {panelComponent}
            </div>
          </div>
        </CSSTransition>
      );
    }

    return (
      <div className={mainClasses} style={style}>
        <Bar
          handleButtonClick={this.handleButtonClick}
          widthSize={widthSize}
          items={{ ...items }}
        />
        {podcastPanel}
        <TransitionGroup component="span">
          {panel}
        </TransitionGroup>
        {children}
      </div>
    );
  }
}

export default RightSidebar;
